import gtm from '../../lib/gtm';

export interface OnboardingStep {
    /** Stable id, also used as the GTM step_id. */
    id: string;
    title: string;
    description: string;
    ctaLabel: string;
    /** Route the CTA navigates to. */
    route: string;
}

/**
 * "Your first project in 10 min" checklist. Completion for each step is derived
 * from a real app signal in use-onboarding, keyed by `id`.
 *
 * Note: a brand-new user already owns a starter example project (created by the
 * backend's BootstrapDefaultProjectForUser), so "create your own project" is
 * detected by the presence of a NON-example project — see isExampleProject.
 */
export const ONBOARDING_STEPS: OnboardingStep[] = [
    {
        id: 'company',
        title: 'Set up your company',
        description: 'Add your company details so your reports and exports are branded correctly.',
        ctaLabel: 'Set up company',
        route: '/company'
    },
    {
        id: 'review_example',
        title: 'Explore the example project',
        description: 'A ready-made GreenRide example is loaded — open Review 1 to see how an analysis looks.',
        ctaLabel: 'Open the example',
        route: '/review-1/customers'
    },
    {
        id: 'create_project',
        title: 'Create your own project',
        description: 'Start your first real innovation analysis with the IPOP methodology.',
        ctaLabel: 'Create project',
        route: '/onboarding'
    },
    {
        id: 'explore',
        title: 'Take the guided tour',
        description: 'Walk through every screen of your workspace, step by step.',
        ctaLabel: 'Start guided tour',
        route: '/'
    }
];

export interface TourScreen {
    /** Matches the sidebar item title / i18n key and the data-tour selector. */
    key: string;
    label: string;
    route: string;
    /** Pathname prefix used to detect a visit ('/' is matched exactly). */
    pathPrefix: string;
}

/** All sidebar screens except project settings — the order the tour walks them. */
export const TOUR_SCREENS: TourScreen[] = [
    { key: 'Dashboard', label: 'Dashboard', route: '/', pathPrefix: '/' },
    { key: 'review_1', label: 'Review 1', route: '/review-1/customers', pathPrefix: '/review-1' },
    { key: 'review_2', label: 'Review 2', route: '/review-2/definition-of-success', pathPrefix: '/review-2' },
    { key: 'review_3', label: 'Review 3', route: '/review-3/factors', pathPrefix: '/review-3' },
    { key: 'review_4', label: 'Review 4', route: '/review-4/moves', pathPrefix: '/review-4' },
    { key: 'unknowns', label: 'Unknowns', route: '/unknowns', pathPrefix: '/unknowns' },
    { key: 'validations', label: 'Validations', route: '/validations', pathPrefix: '/validations' },
    { key: 'reports', label: 'Reports', route: '/reports', pathPrefix: '/reports' }
];

const EXAMPLE_PROJECT_PREFIX = '📘 Example:';

/** The auto-created starter project, identified by its backend-assigned name. */
export const isExampleProject = (project: { name?: string } | null | undefined): boolean =>
    typeof project?.name === 'string' && project.name.startsWith(EXAMPLE_PROJECT_PREFIX);

/** Which tour screen, if any, a pathname belongs to. */
export const matchScreenKey = (pathname: string): string | null => {
    if (pathname === '/') {
        return 'Dashboard';
    }
    const screen = TOUR_SCREENS.find(
        (s) => s.pathPrefix !== '/' && pathname.startsWith(s.pathPrefix)
    );
    return screen ? screen.key : null;
};

const VISITED_KEY = 'ipop:onboarding_visited';
const DISMISSED_KEY = 'ipop:onboarding_dismissed';
const EVENT_KEY_PREFIX = 'ipop:onboarding_evt:';

export const getVisitedScreens = (): string[] => {
    try {
        const raw = window.localStorage.getItem(VISITED_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

export const markScreenVisited = (key: string): void => {
    try {
        const visited = getVisitedScreens();
        if (!visited.includes(key)) {
            window.localStorage.setItem(VISITED_KEY, JSON.stringify([...visited, key]));
        }
    } catch {
        // localStorage unavailable (private mode, quota) — silently ignore
    }
};

export const isOnboardingDismissed = (): boolean => {
    try {
        return window.localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
};

export const markOnboardingDismissed = (): void => {
    try {
        window.localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
        // localStorage unavailable — silently ignore
    }
};

/**
 * Whether the 'started' funnel event was ever recorded for this device — i.e.
 * the user had pending steps at some point. Used to avoid showing the checklist
 * to users who were already fully set up before launch.
 */
export const hasOnboardingStarted = (): boolean => {
    try {
        return window.localStorage.getItem(EVENT_KEY_PREFIX + 'started') === '1';
    } catch {
        return false;
    }
};

/**
 * Push a GTM event at most once ever (per device), so completion-rate funnels
 * are not inflated by re-renders or page reloads. `name` is the idempotency key.
 */
export const emitOnboardingEventOnce = (name: string, payload: object): void => {
    const key = EVENT_KEY_PREFIX + name;
    try {
        if (window.localStorage.getItem(key) === '1') {
            return;
        }
        window.localStorage.setItem(key, '1');
    } catch {
        // localStorage unavailable — fall through and still emit
    }
    gtm.push(payload);
};
